import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';

import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ErknmEvidence } from 'KnmErknmPackage/ErknmReasonsModule/components/ErknmEvidence/ErknmEvidence';
import { removeMaterialsLinkedToVideoFixations } from 'KnmErknmPackage/MainModule/common/cleanupLinkedViolationFormData';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { IKnmErknmViolationEvidence } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmViolationEvidence';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getDecisionIsNew,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import styles from './DecisionViolationEvidence.module.scss';

interface IDecisionViolationEvidenceProps {
  isWatchOnly: boolean;
  formRef: IDecisionFormRef | undefined;
  name: string;
  id: string;
}

interface IDecisionViolationEvidenceStoreProps {
  isDecisionStatusFillInProcess: boolean;
  isNewKnm: boolean;
}

const DecisionViolationEvidenceWithoutStore = ({
  id,
  isWatchOnly,
  formRef,
  isDecisionStatusFillInProcess,
  isNewKnm,
  name,
}: IDecisionViolationEvidenceProps & IDecisionViolationEvidenceStoreProps) => {
  const violationEvidenceFieldName = `${name}.violationEvidences`;
  const isViolationEvidenceFieldName = `${name}.isViolationEvidenceExists`;

  const isDisable = isWatchOnly || (!isNewKnm && !isDecisionStatusFillInProcess);

  const isViolationsEvidenceValue: IBooleanSelectOption = useField(isViolationEvidenceFieldName)?.input?.value;
  const holdingDateTime: string | undefined = useField(`${name}.holdingDateTime`)?.input?.value;

  const parsedHoldingDate = useMemo(() => {
    if (holdingDateTime) {
      return parseDate({ date: holdingDateTime, parseFormat: DATE_FORMAT_LONG_MINUTE_FNS });
    }
  }, [holdingDateTime]);

  const handleIsViolationEvidenceChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value) {
        const evidences = (formRef as ICommonFormRef | undefined)?.current.getFieldState(violationEvidenceFieldName)
          ?.value as IKnmErknmViolationEvidence[] | undefined;
        const guids = evidences?.map(evidence => evidence.guid) || [];

        // @ts-ignore
        formRef?.current.change(violationEvidenceFieldName as keyof IDecisionFormRef);

        // При очистке всего раздела videoFixations убираем связанные материалы нарушений.
        removeMaterialsLinkedToVideoFixations(formRef as ICommonFormRef | undefined, violationEvidenceFieldName, guids);
      }
    },
    [formRef, violationEvidenceFieldName],
  );

  return (
    <div className={styles.GroupMarginBottom}>
      <FieldBlock
        id={`${id}.isViolationEvidenceExists`}
        label={getTitleFieldWithRedStar({
          label: 'Фото (видео) фиксация потенциального нарушения ОТ',
          field: isViolationEvidenceFieldName,
          isDecision: true,
          isKnm: false,
        })}
        size="half"
      >
        <Row>
          <SelectInputField
            className={`shared-row-flex ${styles.SelectInputField}`}
            name={isViolationEvidenceFieldName}
            options={booleanSelectOptions}
            isClearable={false}
            disabled={isDisable}
            onChange={handleIsViolationEvidenceChange}
            size="medium"
          />
        </Row>
      </FieldBlock>
      {isViolationsEvidenceValue.value === true && (
        <ErknmEvidence
          id={`${id}.violationEvidences`}
          name={violationEvidenceFieldName}
          watchOnly={isDisable}
          formRef={formRef}
          isDecision={true}
          isKnm={false}
          isPm={false}
          minDate={parsedHoldingDate}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionViolationEvidenceStoreProps => ({
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  isNewKnm: getDecisionIsNew(state) || false,
});

export const DecisionViolationEvidence = connect(mapStateToProps)(DecisionViolationEvidenceWithoutStore);
